<script setup>
import { computed } from "vue";
import { useRouter } from "vue-router";
import { usePreSportsStore } from "@/stores/pre_sports";
import { useBetBoosterStore } from "@/stores/bet_booster";

const props = defineProps({
	boost: {
		type: Object,
		required: true
	},
	show_sport_ribbon: {
		type: Boolean,
		default: null
	}
});

const router = useRouter();
const preSportsStore = usePreSportsStore();
const betBoosterStore = useBetBoosterStore();

const goToFixture = () => {
	const fixture = props.boost?.fixture || props.boost;
	const sportId = props.boost.sport_id ?? fixture?.sport_id;
	const regionId = props.boost.region_id ?? fixture?.region_id;
	const competitionId = props.boost.competition_id ?? fixture?.competition_id;
	const fixtureId = props.boost.fixture_id ?? props.boost.id ?? fixture?.id;

	if (fixtureId) {
		router.push({
			name: 'pre-sports-fixturebook',
			params: {
				sport_id: sportId,
				region_id: regionId,
				competition_id: competitionId,
				fixture_id: fixtureId
			}
		});
	}
};

const boostPercentage = computed(() => {
	if (props.boost.boost_percentage != null) return props.boost.boost_percentage;

	if (props.boost.original_odds && props.boost.boosted_odds) {
		const diff = props.boost.boosted_odds - props.boost.original_odds;
		return Math.round((diff / props.boost.original_odds) * 100);
	}
	return null;
});

const isSuperBoost = computed(() => {
	if (typeof props.boost.is_super_boost === "boolean") return props.boost.is_super_boost;
	
	return boostPercentage.value >= 20;
});

const shouldShowSportRibbon = computed(() => {
	if (props.show_sport_ribbon !== null && props.show_sport_ribbon !== undefined) return Boolean(props.show_sport_ribbon);
	if (router.currentRoute.value.name === "pre-sports-fixturebook" || router.currentRoute.value.params?.fixture_id != null) return false;
	return betBoosterStore.selected_sport_id === "all";
});

const sportIcon = computed(() => {
	if (props.boost?.sport_id && preSportsStore.preSportWhiteIcon) return preSportsStore.preSportWhiteIcon(props.boost.sport_id);
	return null;
});

const sportName = computed(() => {
	if (props.boost?.sport_name) return props.boost.sport_name;
	if (props.boost?.sport_id && preSportsStore.pre_sports) {
		const found = preSportsStore.pre_sports.find((s) => String(s.id) === String(props.boost.sport_id));
		if (found && found.name) return found.name;
	}
	if (props.boost?.sport_id && preSportsStore.preSportName) {
		return preSportsStore.preSportName(props.boost.sport_id);
	}
	return "";
});

const getTeamInitial = (name) => {
	if (!name) return "";
	return name.trim().charAt(0).toUpperCase();
};

const onFlagError = (e) => {
	e.target.style.display = "none";
	if (e.target.nextElementSibling) {
		e.target.nextElementSibling.style.display = "flex";
	}
};
</script>

<template>
	<div class="bet-booster-card-header">
		<!-- Top Ribbon -->
		<div class="boost-ribbon" :class="{'super-boost': isSuperBoost, 'standard-boost': !isSuperBoost}">
			<div class="ribbon-label">
				<span class="ribbon-icon">{{ isSuperBoost ? "🚀" : "🔥" }}</span>
				<span class="ribbon-text">
					{{ isSuperBoost ? ($t("bet_boost.super_boosted") || "SUPER BOOSTED") : ($t("bet_boost.boosted") || "BOOSTED") }}
				</span>
			</div>

			<div class="ribbon-right">
				<!-- Sports Icon & Name Ribbon Badge -->
				<div v-if="shouldShowSportRibbon && (sportName || sportIcon)" class="ribbon-sport">
					<svg v-if="sportIcon" class="ribbon-sport-svg" xmlns="http://www.w3.org/2000/svg" xml:space="preserve" version="1.1" viewBox="0 0 30 25" xmlns:xlink="http://www.w3.org/1999/xlink">
						<use :xlink:href="sportIcon + '#svg3108'" stroke-width="1.2px" />
					</svg>
					<span v-else class="ribbon-sport-fallback">🎯</span>
					<span v-if="sportName" class="ribbon-sport-name">{{ sportName }}</span>
				</div>

				<!-- Boost Percentage Badge -->
				<div v-if="boostPercentage" class="ribbon-percentage">
					+{{ boostPercentage }}%
				</div>
			</div>
		</div>

		<!-- Match Event Header -->
		<div v-if="boost.type === 'match'" class="event-header match-header">
			<div class="flag-container left">
				<img v-if="boost.team1 && (boost.team1.logo || boost.team1.flag)" :src="boost.team1.logo || boost.team1.flag" :alt="boost.team1.name || 'Team 1'" class="team-flag" @error="onFlagError"/>
				<div class="flag-placeholder" :style="{ display: (boost.team1 && (boost.team1.logo || boost.team1.flag)) ? 'none' : 'flex' }">
					<span>{{ getTeamInitial(boost.team1?.name) }}</span>
				</div>
			</div>

			<div class="event-center-info" @click="goToFixture">
				<div class="competition-name" :title="boost.competition || boost.competition_name">{{ boost.competition || boost.competition_name }}</div>
				<div class="event-title" :title="boost.title">{{ boost.title }}</div>
				<div v-if="boost.start_time" class="event-time">{{ boost.start_time }}</div>
			</div>

			<div class="flag-container right">
				<img v-if="boost.team2 && (boost.team2.logo || boost.team2.flag)" :src="boost.team2.logo || boost.team2.flag" :alt="boost.team2.name || 'Team 2'" class="team-flag" @error="onFlagError"/>
				<div class="flag-placeholder" :style="{ display: (boost.team2 && (boost.team2.logo || boost.team2.flag)) ? 'none' : 'flex' }">
					<span>{{ getTeamInitial(boost.team2?.name) }}</span>
				</div>
			</div>
		</div>

		<!-- Outright / Special Event Header -->
		<div v-else class="event-header special-header">
			<div class="special-top-row">
				<div class="special-title" :title="boost.title">{{ boost.title }}</div>
				<div v-if="boost.badge || boost.is_exclusive" class="exclusive-badge">
					<span class="crown-icon">👑</span>
					<span class="exclusive-text">{{ boost.badge || ("bet_boost.exclusive") || "EXCLUSIVE" }}</span>
				</div>
			</div>
		</div>
	</div>
</template>

<style lang="scss" scoped>
.bet-booster-card-header {
	width: 100%;
}

.boost-ribbon {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 6px 12px;
	color: var(--white);
	font-size: 10.5px;
	font-weight: 800;
	letter-spacing: 0.5px;
	text-transform: uppercase;
	text-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
	border-top-left-radius: 12px;
	border-top-right-radius: 12px;

	&.super-boost {
		background: linear-gradient(135deg, var(--secondary) 0%, var(--odd-price-down) 100%);
	}

	&.standard-boost {
		background: var(--primary);
	}

	.ribbon-label {
		display: flex;
		align-items: center;
		gap: 5px;

		.ribbon-icon {
			font-size: 11px;
		}

		.ribbon-text {
			font-weight: 700;
		}
	}

	.ribbon-right {
		display: flex;
		align-items: center;
		gap: 6px;
	}

	.ribbon-sport {
		display: inline-flex;
		align-items: center;
		gap: 4px;
		background: rgba(0, 0, 0, 0.22);
		padding: 1px 7px;
		border-radius: 6px;
		white-space: nowrap;

		.ribbon-sport-svg {
			width: 14px;
			height: 14px;
			fill: none;
			stroke: var(--white);
			display: block;
			flex-shrink: 0;
		}

		.ribbon-sport-fallback {
			font-size: 11px;
			line-height: 1;
		}

		.ribbon-sport-name {
			font-size: 10.5px;
			font-weight: 700;
			color: var(--white);
			letter-spacing: 0.2px;
		}
	}

	.ribbon-percentage {
		font-size: 11px;
		font-weight: 800;
		background: rgba(0, 0, 0, 0.22);
		padding: 1px 7px;
		border-radius: 6px;
		white-space: nowrap;
	}
}

.event-header {
	padding: 9px 12px 6px 12px;

	&.match-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 8px;

		.flag-container {
			flex-shrink: 0;
			width: 28px;
			height: 28px;
			border-radius: 50%;
			overflow: hidden;
			display: flex;
			align-items: center;
			justify-content: center;
			background: rgba(255, 255, 255, 0.05);
			// box-shadow: 0 1px 4px rgba(0, 0, 0, 0.16);
			// border: 1px solid rgba(255, 255, 255, 0.08);
			// padding: 3px;
			box-sizing: border-box;

			.team-flag {
				width: 100%;
				height: 100%;
				object-fit: contain;
				display: block;
			}

			.flag-placeholder {
				width: 100%;
				height: 100%;
				border-radius: 50%;
				background: linear-gradient(135deg, rgba(255, 255, 255, 0.12), rgba(255, 255, 255, 0.04));
				display: flex;
				align-items: center;
				justify-content: center;
				font-size: 11.5px;
				font-weight: 700;
				color: var(--white);
				user-select: none;
			}
		}

		.event-center-info {
			flex: 1;
			text-align: center;
			min-width: 0;
			cursor: pointer;

			.competition-name {
				font-size: 9.5px;
				color: var(--white);
				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;
				line-height: 1.2;
				font-weight: 500;
			}

			.event-title {
				font-size: 12px;
				font-weight: 700;
				color: var(--white);
				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;
				margin: 1px 0;
				line-height: 1.2;
			}

			.event-time {
				font-size: 9px;
				color: var(--white);
				font-weight: 500;
				line-height: 1.2;
			}
		}
	}

	&.special-header {
		.special-top-row {
			display: flex;
			align-items: center;
			justify-content: space-between;
			gap: 8px;

			.special-title {
				font-size: 12px;
				font-weight: 800;
				color: var(--white);
				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;
			}

			.exclusive-badge {
				display: flex;
				flex-direction: column;
				align-items: center;
				justify-content: center;
				font-size: 8px;
				font-weight: 800;
				color: var(--white);
				text-transform: uppercase;
				letter-spacing: 0.3px;
				line-height: 1;
				gap: 2px;

				.crown-icon {
					font-size: 12px;
				}

				.exclusive-text {
					font-size: 7.5px;
					font-weight: 900;
				}
			}
		}
	}
}

html[dir="rtl"] .ribbon-sport-name {
	margin-top: 1px;
}

@media (max-width: 991px) {
	.boost-ribbon {
		padding: 5px 10px;
		font-size: 10px;
		border-top-left-radius: 10px;
		border-top-right-radius: 10px;

		.ribbon-label {
			gap: 4px;

			.ribbon-icon {
				font-size: 10px;
			}
		}

		.ribbon-sport {
			padding: 1px 5px;
			gap: 3px;

			.ribbon-sport-svg {
				width: 12px;
				height: 12px;
			}

			.ribbon-sport-name {
				font-size: 9.5px;
			}
		}

		.ribbon-percentage {
			font-size: 10px;
			padding: 1px 5px;
		}
	}

	.event-header {
		padding: 8px 10px 4px 10px;

		&.match-header {
			gap: 6px;

			.flag-container {
				width: 26px;
				height: 26px;

				.flag-placeholder {
					font-size: 10.5px;
				}
			}

			.event-center-info {
				.competition-name {
					font-size: 9px;
					line-height: 1.1;
				}

				.event-title {
					font-size: 11.5px;
					margin: 1px 0;
					line-height: 1.2;
				}

				.event-time {
					font-size: 8.5px;
				}
			}
		}

		&.special-header {
			.special-top-row {
				.special-title {
					font-size: 11.5px;
				}
			}
		}
	}
}

@media (max-width: 425px) {
	.event-header {
		padding: 8px 10px 4px 10px;
	}
}
</style>
